import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

import {
  ALLOWED_FILE_TYPES,
  DEFAULT_PAGE_SIZE,
  IMAGE_EXTENSIONS,
  MAX_FILE_SIZE,
  PREVIEWABLE_EXTENSIONS,
} from '@/constants';

import type { PaginationParams } from '@/types/common.type';

export const buildQueryParams = (
  params?: PaginationParams,
  extra?: Record<string, unknown>
): Record<string, unknown> => ({
  page: params?.page || 1,
  page_size: params?.page_size || DEFAULT_PAGE_SIZE,
  ...(params?.query && { query: params.query }),
  ...extra,
});

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const copyToClipboard = async (value: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    return false;
  }
};

// Common utilities to avoid duplication across components
export const generateDocumentId = () =>
  `doc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

export const getCurrentUser = (userData: { firstName: string; lastName: string }) =>
  `${userData.firstName} ${userData.lastName}`;

// Common file size formatting utility
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const getFileType = (file: File): string => {
  const extension = file.name.split('.').pop()?.toLowerCase() || '';
  return extension;
};

export const getFileName = (filePath: string): string => {
  return filePath.split('/').pop() || filePath;
};

export const getFileExtension = (filePath: string): string => {
  const fileName = getFileName(filePath);
  const parts = fileName.split('.');
  return parts.length > 1 ? (parts.pop()?.toUpperCase() ?? 'FILE') : 'FILE';
};

export const isImageFile = (extension: string): boolean => {
  return IMAGE_EXTENSIONS.includes(extension.toLowerCase());
};

export const isPreviewableFile = (extension: string): boolean => {
  return PREVIEWABLE_EXTENSIONS.includes(extension.toLowerCase());
};

export const validateFile = (file: File): string | null => {
  const fileType = getFileType(file);

  if (!ALLOWED_FILE_TYPES.includes(fileType)) {
    return `File type ".${fileType}" is not supported. Allowed types: ${ALLOWED_FILE_TYPES.join(', ')}`;
  }

  if (file.size > MAX_FILE_SIZE) {
    return `File size (${formatFileSize(file.size)}) exceeds the maximum limit of ${formatFileSize(MAX_FILE_SIZE)}`;
  }

  if (!file.name.trim()) {
    return 'File name cannot be empty';
  }

  return null;
};

export const validateUrl = (urlString: string): boolean => {
  try {
    const url = new URL(urlString);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

export const normalizePayloadData = (payload: unknown) => {
  if (typeof payload !== 'string') {
    return payload;
  }

  try {
    return JSON.parse(payload);
  } catch {
    return payload;
  }
};
